import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
} from '@mui/material';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';

import FormActionButtons from 'components/ui/form/FormActionButtons';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useNotification from 'hooks/useNotification';
import BankCustomerSearch from './BankCustomerSearch';

const validationSchema = yup.object({
  contract_id: yup.number().required('payment.addModal.validation.sum'),
});

const SetContractModal = (props) => {
  const { open, setOpen, refetch, data: contractData } = props;
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const sendNotification = useNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  // Oyna ochilganda, to'lov izohidan (nazpla) topilgan shartnoma raqamiga
  // mos nomzodlarni o'zimiz taklif qilamiz — xodim qidirmasdan tanlasin
  useEffect(() => {
    if (!open || !contractData?.id) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;
    setIsLoadingSuggestions(true);
    axiosPrivate
      .get(`/admin/bank/suggest/${contractData.id}`)
      .then((response) => {
        if (!cancelled && response?.data?.status) {
          setSuggestions(response.data.data || []);
        }
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSuggestions(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractData?.id]);

  const formik = useFormik({
    initialValues: {
      contract_id: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        setIsSubmitting(true);
        const response = await axiosPrivate.post(
          `/admin/bank/connectcontact/${contractData?.id}`,
          JSON.stringify(values),
          { headers: { 'Content-Type': 'application/json' } }
        );
        if (response.data && response.data.status) {
          sendNotification({
            msg: response?.data?.message,
            variant: 'success',
          });
          setIsSubmitting(false);
          handleClose();
          refetch();
        }
      } catch (error) {
        sendNotification({
          msg: error?.response?.data?.message || error?.message,
          variant: 'error',
        });
        setIsSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  const handleSelectSuggestion = (item) => {
    formik.setFieldValue('contract_id', item?.id);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='lg'
      disableEscapeKeyDown={true}
    >
      <DialogTitle id='alert-dialog-title'>
        <span className='pr-5'>{t('payment.addModal.title')}</span>
        <div className='close-btn-wrapper'>
          <IconButton variant='onlyIcon' color='primary' onClick={handleClose}>
            <i className='bi bi-x' />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent className='!w-[500px]'>
        <form onSubmit={formik.handleSubmit}>
          <Grid
            container
            spacing={{ xs: 2, sm: 2 }}
            rowSpacing={1}
            columns={{ xs: 12, sm: 12 }}
          >
            {contractData?.nazpla ? (
              <Grid item={true} sm={12} xs={12}>
                <div>
                  <p className='text-gray-500 text-sm'>
                    {t('bank.view.comment')}
                  </p>
                  <span className='text-gray-800 text-base font-medium'>
                    {contractData.nazpla}
                  </span>
                </div>
              </Grid>
            ) : null}

            {isLoadingSuggestions ? (
              <Grid item={true} sm={12} xs={12}>
                <div className='flex items-center justify-center py-2'>
                  <CircularProgress size={20} color='inherit' />
                </div>
              </Grid>
            ) : suggestions.length > 0 ? (
              <Grid item={true} sm={12} xs={12}>
                <div className='text-xs text-gray-500 mb-1'>
                  {t('payment.addModal.suggestedContracts')}
                </div>
                {suggestions.map((item) => (
                  <MenuItem
                    key={item?.id}
                    selected={formik.values.contract_id == item?.id}
                    onClick={() => handleSelectSuggestion(item)}
                    className='!rounded-md !border !border-solid !border-gray-200 !mb-1'
                  >
                    <div className='text-[12px] w-full'>
                      <div className='flex justify-between items-center'>
                        <span>{t('common.table.custom')}:</span>
                        <span>{item?.fullname?.replaceAll('-', ' ')}</span>
                      </div>
                      <div className='flex justify-between items-center'>
                        <span>{t('common.fields.phone')}:</span>
                        <span>{item?.phone}</span>
                      </div>
                      <div className='flex justify-between items-center'>
                        <span>{t('common.table.paymentContract')}:</span>
                        <span>{item?.name}</span>
                      </div>
                    </div>
                  </MenuItem>
                ))}
              </Grid>
            ) : null}

            <Grid item={true} sm={12} xs={12}>
              <BankCustomerSearch formik={formik} />
            </Grid>

            <Grid item={true} sm={12} xs={12}>
              <FormActionButtons
                delay={0.5}
                isSubmitting={isSubmitting}
                formType='dialog'
                setOpen={setOpen}
                reset={formik.resetForm}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(SetContractModal);
